import React from "react";
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    Button,
    LinearProgress,
    Avatar,
    IconButton,
    Grid,
    Divider,
} from "@mui/material";
import {
    Award,
    TrendingUp,
    FileText,
    HelpCircle,
    Download,
    Filter,
    ArrowLeft,
    ChevronRight,
    Target,
    BarChart3,
} from "lucide-react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";

const GradesView = () => {
    const navigate = useNavigate();

    const gradeReport = [
        {
            course: "Advanced Algebra & Calculus",
            weight: "100%",
            grade: "88%",
            range: "0-100",
            letter: "A-",
            feedback: "Great work on differentiation, focus more on complex integration next time.",
            activities: [
                { name: "Complex Plane Quiz", grade: "92%", weight: "10%", type: "quiz" },
                { name: "Differentiation PS #1", grade: "85%", weight: "15%", type: "assignment" },
                { name: "Midterm Exam", grade: "87%", weight: "25%", type: "exam" },
            ]
        },
        {
            course: "Quantum Physics Fundamentals",
            weight: "100%",
            grade: "74%",
            range: "0-100",
            letter: "B-",
            feedback: "Maintain consistent practice in wave functions.",
            activities: [
                { name: "Wave Function Quiz", grade: "68%", weight: "10%", type: "quiz" },
                { name: "Schrodinger Homework", grade: "80%", weight: "15%", type: "assignment" },
            ]
        },
    ];

    return (
        <Box sx={{ p: 4, bgcolor: "#f1f5f9", minHeight: "100vh" }}>
            <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }}>
                {/* Header Section */}
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 4, alignItems: "center" }}>
                    <Box>
                        <Typography variant="h4" sx={{ fontWeight: 800, color: "#1e293b", mb: 1 }}>Grade Report</Typography>
                        <Typography variant="body1" sx={{ color: "#64748b" }}>Track your performance across all tutoring courses.</Typography>
                    </Box>
                    <Box sx={{ display: "flex", gap: 2 }}>
                        <Button variant="outlined" startIcon={<Download size={18} />} sx={{ borderRadius: "12px", textTransform: "none", fontWeight: 700, borderColor: "#e2e8f0", color: "#1e293b" }}>Export Report</Button>
                        <Button variant="contained" startIcon={<Filter size={18} />} sx={{ borderRadius: "12px", bgcolor: "#4f46e5", textTransform: "none", fontWeight: 700 }}>Filter by Period</Button>
                    </Box>
                </Box>

                {/* Global Progress Hub */}
                <Grid container spacing={3} sx={{ mb: 4 }}>
                    {[
                        { label: "Overall GPA", value: "3.8", icon: <Award size={24} />, color: "#4f46e5" },
                        { label: "Total Assignments", value: "24", icon: <FileText size={24} />, color: "#06b6d4" },
                        { label: "Completion Rate", value: "92%", icon: <Target size={24} />, color: "#10b981" },
                        { label: "Tutor Feedback", value: "11 New", icon: <BarChart3 size={24} />, color: "#f59e0b" },
                    ].map((stat, idx) => (
                        <Grid item xs={12} sm={6} md={3} key={idx}>
                            <Paper sx={{ p: 3, borderRadius: "24px", display: "flex", alignItems: "center", gap: 3, border: "1px solid #f1f5f9" }}>
                                <Box sx={{ p: 1.5, borderRadius: "12px", bgcolor: `${stat.color}15`, color: stat.color, display: "flex" }}>
                                    {stat.icon}
                                </Box>
                                <Box>
                                    <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 700, display: "block", textTransform: "uppercase" }}>{stat.label}</Typography>
                                    <Typography variant="h5" sx={{ fontWeight: 800, color: "#1e293b" }}>{stat.value}</Typography>
                                </Box>
                            </Paper>
                        </Grid>
                    ))}
                </Grid>

                {/* User Evaluation Report - Table View */}
                {gradeReport.map((course, cIdx) => (
                    <Paper key={cIdx} sx={{ borderRadius: "32px", overflow: "hidden", mb: 4, border: "1px solid #e2e8f0", boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
                        <Box sx={{ p: 4, bgcolor: "#fff", borderBottom: "1px solid #f1f5f9", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                <Box sx={{ width: 48, height: 48, borderRadius: "14px", bgcolor: "#f8fafc", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #e2e8f0" }}>
                                    <TrendingUp size={24} color="#4f46e5" />
                                </Box>
                                <Typography variant="h5" sx={{ fontWeight: 800, color: "#1e293b" }}>{course.course}</Typography>
                            </Box>
                            <Box sx={{ display: "flex", gap: 3, alignItems: "center" }}>
                                <Box sx={{ textAlign: "right" }}>
                                    <Typography variant="caption" sx={{ fontWeight: 800, color: "#64748b", display: "block" }}>COURSE GRADE</Typography>
                                    <Typography variant="h4" sx={{ fontWeight: 900, color: course.letter.includes("A") ? "#10b981" : "#f59e0b" }}>{course.letter}</Typography>
                                </Box>
                                <Divider orientation="vertical" flexItem />
                                <Box sx={{ textAlign: "right" }}>
                                    <Typography variant="caption" sx={{ fontWeight: 800, color: "#64748b", display: "block" }}>PERCENTAGE</Typography>
                                    <Typography variant="h4" sx={{ fontWeight: 900 }}>{course.grade}</Typography>
                                </Box>
                            </Box>
                        </Box>

                        <TableContainer>
                            <Table>
                                <TableHead sx={{ bgcolor: "#f8fafc" }}>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 800, color: "#64748b", py: 2.5 }}>Activity Name</TableCell>
                                        <TableCell sx={{ fontWeight: 800, color: "#64748b" }}>Grade</TableCell>
                                        <TableCell sx={{ fontWeight: 800, color: "#64748b" }}>Range</TableCell>
                                        <TableCell sx={{ fontWeight: 800, color: "#64748b" }}>Percentage</TableCell>
                                        <TableCell sx={{ fontWeight: 800, color: "#64748b" }}>Feedback</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {course.activities.map((row, rIdx) => (
                                        <TableRow key={rIdx} sx={{ "&:hover": { bgcolor: "#f8fafc" } }}>
                                            <TableCell sx={{ py: 3 }}>
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                                    {row.type === "quiz" ? <HelpCircle size={18} color="#f59e0b" /> : <FileText size={18} color="#06b6d4" />}
                                                    <Typography variant="body2" sx={{ fontWeight: 700, color: "#1e293b" }}>{row.name}</Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ fontWeight: 800, color: "#334155" }}>{row.grade}</TableCell>
                                            <TableCell sx={{ color: "#64748b", fontWeight: 600 }}>0-100</TableCell>
                                            <TableCell>
                                                <Box sx={{ width: "100%", display: "flex", alignItems: "center", gap: 1.5 }}>
                                                    <Box sx={{ flex: 1 }}>
                                                        <LinearProgress
                                                            variant="determinate"
                                                            value={parseInt(row.grade)}
                                                            sx={{
                                                                height: 6,
                                                                borderRadius: 3,
                                                                bgcolor: "#f1f5f9",
                                                                "& .MuiLinearProgress-bar": { bgcolor: parseInt(row.grade) > 85 ? "#10b981" : "#4f46e5" }
                                                            }}
                                                        />
                                                    </Box>
                                                    <Typography variant="caption" sx={{ fontWeight: 800 }}>{row.grade}</Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ color: "#64748b", maxWidth: "250px" }}>
                                                <Typography variant="caption" sx={{ fontStyle: "italic", lineHeight: 1.5 }}>
                                                    "Exceptional clarity in derivations and structured proof..."
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    <TableRow sx={{ bgcolor: "#4f46e505" }}>
                                        <TableCell colSpan={5} sx={{ p: 3 }}>
                                            <Box sx={{ bgcolor: "#4f46e510", p: 3, borderRadius: "18px", border: "1px solid #4f46e515" }}>
                                                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#4f46e5", mb: 1, textTransform: "uppercase" }}>Tutor Feedback Overall</Typography>
                                                <Typography variant="body2" sx={{ color: "#334155", fontWeight: 600, fontStyle: "italic" }}>
                                                    {course.feedback}
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                ))}
            </motion.div>
        </Box>
    );
};

export default GradesView;
